import mixpanel from "mixpanel-browser";
import { appConfig } from "src/appConfig";
import { AppContextState } from "src/AppContextProvider";
import { Plan } from "src/billing/types";
import { getPlanNameWithoutCurrency } from "src/helpers/planHelper";

export const shouldDoMixpanel = () => {
  return (
    appConfig.studioPlayerEnvironment !== "development" &&
    appConfig.studioPlayerEnvironment !== "edge"
  );
};

export const getMixpanelPlanTier = (
  context: AppContextState,
): string | undefined => {
  const subscription = context.subscription;
  if (!subscription) {
    return undefined;
  }

  if (
    "billingPlanByPlanId" in subscription &&
    subscription.billingPlanByPlanId
  ) {
    const plan = subscription.billingPlanByPlanId as Pick<
      Plan,
      "cfName" | "cfIncludedPhysicalScreenLicenses" | "name"
    >;
    return getPlanNameWithoutCurrency(plan);
  }

  if ("planId" in subscription && subscription.planId) {
    const tier = subscription.planId.split("-")[0];
    return `${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
  }

  return undefined;
};

export const getMixpanelDaysIntoTrial = (
  context: AppContextState,
): number | undefined => {
  const subscription = context.subscription;
  if (
    !subscription ||
    !("trialStart" in subscription) ||
    !subscription.trialStart
  ) {
    return undefined;
  }

  return Math.floor((Date.now() / 1000 - subscription.trialStart) / 86400);
};

export const getPublishUsageMetrics = ({
  createdAt,
  publishedAt,
  itemCount,
}: {
  createdAt?: string | null;
  publishedAt?: string | null;
  itemCount: number;
}) => {
  const now = Date.now();
  const createdTime = createdAt ? new Date(createdAt).getTime() : null;
  const lastPublishedTime = publishedAt
    ? new Date(publishedAt).getTime()
    : null;

  return {
    item_count: itemCount,
    minutes_to_publish:
      createdTime != null ? Math.round((now - createdTime) / 60000) : undefined,
    is_republish: lastPublishedTime != null,
    days_since_last_publish:
      lastPublishedTime != null
        ? Math.round((now - lastPublishedTime) / 86400000)
        : undefined,
  };
};

export type PairingCodeSource = "url" | "prop";

export const getPairingCodeTrackingProperties = ({
  usedUrlPrefill,
  defaultPairingCode,
  pairingPath,
}: {
  usedUrlPrefill: boolean;
  defaultPairingCode?: string;
  pairingPath?: string;
}): Record<string, string> => {
  const properties: Record<string, string> = {};

  if (usedUrlPrefill) {
    properties.pairing_method = "prefilled_code";
    properties.pairing_code_source = "url";
  } else if (defaultPairingCode) {
    properties.pairing_method = "prefilled_code";
    properties.pairing_code_source = "prop";
  } else {
    properties.pairing_method = "manual_code";
  }

  if (pairingPath) {
    properties.pairing_path = pairingPath;
  }

  return properties;
};

export const getPairingStartedTrackingProperties = (
  pairingPath: string,
): Record<string, string> => ({
  pairing_path: pairingPath,
});

export const ONBOARDING_STEPS = [
  "design_screen_content",
  "pair_screen",
  "set_content_to_screen",
] as const;

export type OnboardingStepName = (typeof ONBOARDING_STEPS)[number];

export type StudioOnboardingFlow = "standard" | "pixi_bundle";

const TOTAL_ONBOARDING_STEPS = ONBOARDING_STEPS.length;

export const trackOnboardingStepEvent = ({
  context,
  status,
  stepName,
  stepNumber,
  onboardingFlow,
}: {
  context: AppContextState;
  status: "started" | "completed";
  stepName: OnboardingStepName;
  stepNumber: number;
  onboardingFlow: StudioOnboardingFlow;
}) => {
  trackMixpanelEvent({
    event:
      status === "started"
        ? "Studio Onboarding Step Started"
        : "Studio Onboarding Step Completed",
    context,
    properties: {
      onboarding_flow: onboardingFlow,
      step_name: stepName,
      step_number: stepNumber,
    },
  });

  const prefs = context.currentOrg?.preferences.pixi_trialist;
  const shouldTrackFlowCompletion =
    status === "completed" &&
    stepNumber === TOTAL_ONBOARDING_STEPS &&
    prefs?.completed_design_screen_content === true &&
    prefs?.completed_pair_screen === true;

  if (shouldTrackFlowCompletion) {
    trackMixpanelEvent({
      event: "Studio Onboarding Completed",
      context,
      properties: {
        onboarding_flow: onboardingFlow,
        total_steps: TOTAL_ONBOARDING_STEPS,
      },
    });
  }
};

export type OnboardingPlanSelectionType = "plan" | "billing_cycle" | "deal";

export const trackOnboardingPlanSelectionEvent = ({
  context,
  status,
  planTier,
  billingCycle,
  deal,
  licenses,
  shippingCountry,
  selectionType,
  selectionValue,
}: {
  context: AppContextState;
  status: "selected" | "completed";
  planTier: string;
  billingCycle: string;
  deal?: string;
  licenses: number;
  shippingCountry?: string;
  selectionType?: OnboardingPlanSelectionType;
  selectionValue?: string;
}) => {
  trackMixpanelEvent({
    event:
      status === "selected"
        ? "Studio Onboarding Plan Option Selected"
        : "Studio Onboarding Plan Selection Completed",
    context,
    properties: {
      onboarding_flow: "pixi_bundle",
      plan_tier: planTier,
      billing_cycle: billingCycle,
      deal: deal ?? "none",
      licenses,
      ...(shippingCountry !== undefined && {
        shipping_country: shippingCountry,
      }),
      ...(selectionType !== undefined && {
        selection_type: selectionType,
      }),
      ...(selectionValue !== undefined && {
        selection_value: selectionValue,
      }),
    },
  });
};

export const initMixpanel = (context: AppContextState) => {
  if (shouldDoMixpanel()) {
    mixpanel.init(appConfig.mixpanelToken, {
      autocapture: false,
      debug: true,
    });
    // as we init mixpanel once user login, so we can just track the event once here too
    trackMixpanelEvent({
      event: "User Login",
      context,
    });
  }
};

export const trackMixpanelEvent = ({
  event,
  properties,
  context,
}: {
  event: string;
  properties?: Record<string, any>;
  context: AppContextState;
}) => {
  if (shouldDoMixpanel()) {
    mixpanel.track(event, {
      ...properties,
      distinct_id: context.currentUser?.id,
      org_id: context.currentOrg?.id,
      env: appConfig.studioPlayerEnvironment,
    });
  }
};
